import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Car, ArrowRight, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F6FA]">
      <div className="hidden lg:flex relative bg-gradient-to-br from-[#1A2A6C] via-[#2D3F8F] to-[#3D4FA0] p-12 items-center justify-center overflow-hidden text-white">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-40 -left-20 w-[28rem] h-[28rem] rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10 max-w-md anim-slide-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-semibold mb-6">
            <Mail className="w-3.5 h-3.5" /> Recupero password
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Nessun problema, <span className="text-[#FF9E1B]">ti aiutiamo</span>.
          </h1>
          <p className="text-lg font-medium text-white/85 leading-relaxed">
            Inserisci l'email del tuo account: ti invieremo un link sicuro per impostare una nuova password.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md anim-slide-up">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A6C] hover:underline mb-6" data-testid="forgot-back-link">
            <ArrowLeft className="w-4 h-4" /> Torna al login
          </Link>

          <div className="inline-flex items-center gap-2.5 mb-8">
            <img src="/hopzigo-logo.png" alt="Hopzigo" className="h-12 w-auto" />
          </div>

          {sent ? (
            <div className="brut-card p-6 sm:p-8 anim-pop-in" data-testid="forgot-success-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#D4F1ED] text-[#26B5A8]">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h2 className="font-heading text-2xl font-bold">Controlla la tua email</h2>
              </div>
              <p className="text-[#525252] mb-2">
                Se <span className="font-semibold text-[#1A1D29]">{email}</span> è registrata, riceverai un link per impostare una nuova password.
              </p>
              <p className="text-sm text-[#8A91A8]">Il link è valido per 30 minuti.</p>
              <div className="mt-6 flex gap-3">
                <button type="button" onClick={() => { setSent(false); setEmail(""); }} className="brut-btn brut-btn-ghost" data-testid="forgot-resend">
                  Invia di nuovo
                </button>
                <button type="button" onClick={() => nav("/login")} className="brut-btn flex-1" data-testid="forgot-go-login">
                  Torna al login <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Password dimenticata?</h2>
              <p className="text-[#8A91A8] mb-8">Inseriscila e ti manderemo un link sicuro.</p>

              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label className="brut-label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    className="brut-input"
                    placeholder="mario@famiglia.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                <button type="submit" className="brut-btn w-full text-base" disabled={loading} data-testid="forgot-submit-button">
                  {loading ? "Invio…" : (<>Invia link <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </form>

              <p className="mt-8 text-sm text-[#8A91A8]">
                Ti sei ricordato?{" "}
                <Link to="/login" className="font-semibold text-[#1A2A6C] hover:underline" data-testid="forgot-login-link">
                  Accedi
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
